import React, { useState, useEffect, useCallback } from "react";
import { Card, Select, Button, Spin, message } from "antd";
import { LeftCircleFilled, RightCircleFilled } from "@ant-design/icons";
import moment from "moment";
import { getGroups, getFacilitiesByGroup, getFacilityPeriodicalOccurrence } from "./periodicalApi";
import "./FacilityMultiSelect.css";

const { Option } = Select;

// Left-edge colours of the old app's status cards (sv-card--*), by status id.
const STATUS_BORDER = { 1: "#fdd835", 2: "#42c7fe", 3: "#ffa726", 4: "#d50000", 5: "#8bc34a" };
const COLUMNS = [
  { key: "completed", title: "Completed" },
  { key: "due", title: "Due" },
  { key: "upcoming", title: "Upcoming" },
];

// Same split as the old app: Completed by status, then the rest by date —
// already started is Due, still ahead is Upcoming.
const toColumns = (months) => {
  const columns = { completed: [], due: [], upcoming: [] };
  const now = moment();
  months.forEach((m) => (m.data || []).forEach((task) => {
    if (task.status === 5) columns.completed.push(task);
    else if (moment(task.planned_start_date_time).isAfter(now)) columns.upcoming.push(task);
    else columns.due.push(task);
  }));
  return columns;
};

const TaskCard = ({ task }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e8eaee",
      borderLeft: `4px solid ${STATUS_BORDER[task.status] || "#d9d9d9"}`,
      borderRadius: 6,
      padding: "10px 12px",
      marginBottom: 8,
    }}
  >
    <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{task.periodical_name}</div>
    <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
      {moment(task.planned_start_date_time).format("DD MMM")}
      {task.contract_facility_name ? ` • ${task.contract_facility_name}` : ""}
    </div>
    {Array.isArray(task.user_list) && task.user_list.length > 0 && (
      <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
        {task.user_list.map((u) => (
          <span
            key={u.id ?? u.user_id ?? u.user_name}
            title={u.user_name}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "#743fbc",
              color: "#fff",
              fontSize: 10,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {(u.user_name || "?").slice(0, 1).toUpperCase()}
          </span>
        ))}
      </div>
    )}
  </div>
);

// Read-only board like the old app's Status View: Group, one or more of its
// facilities, a year stepper, and Completed / Due / Upcoming columns.
const StatusView = () => {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [facilityIds, setFacilityIds] = useState([]);
  const [year, setYear] = useState(moment().year());
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState(null);

  useEffect(() => {
    (async () => {
      const { error, result } = await getGroups();
      setGroups(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const onGroupChange = useCallback(async (value) => {
    setGroupId(value);
    setFacilityIds([]);
    setFacilities([]);
    setColumns(null);
    const { error, result } = await getFacilitiesByGroup(value);
    setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const load = useCallback(async (forYear) => {
    if (!groupId || facilityIds.length === 0) {
      message.error("Please select a group and at least one facility first.");
      return;
    }
    setLoading(true);
    const { error, result } = await getFacilityPeriodicalOccurrence({
      year: forYear,
      contractFacilityId: facilityIds.join(","),
      userId: localStorage.getItem("user_id"),
    });
    setColumns(toColumns(!error && Array.isArray(result?.result) ? result.result : []));
    setLoading(false);
  }, [groupId, facilityIds]);

  // As in the old app, stepping the year reloads straight away once facilities are chosen.
  const stepYear = (delta) => {
    const next = year + delta;
    setYear(next);
    if (columns) load(next);
  };

  const facilityValue = (f) => f.facility_id ?? f.id;

  return (
    <Card bodyStyle={{ padding: 0 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: 16, borderBottom: "1px solid #f0f0f0" }}>
        <Select showSearch optionFilterProp="children" placeholder="Select a group" style={{ width: 200 }} value={groupId} onChange={onGroupChange}>
          {groups.map((g) => <Option key={g.id} value={g.id}>{g.name}</Option>)}
        </Select>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="children"
          placeholder="Select facility…"
          className="ws-facility-multi"
          style={{ flex: "1 1 320px", minWidth: 0 }}
          value={facilityIds}
          onChange={setFacilityIds}
          disabled={!groupId}
          maxTagCount={6}
          maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
          notFoundContent={groupId ? "No facilities for this group" : "Select a group first"}
        >
          {facilities.map((f) => <Option key={facilityValue(f)} value={facilityValue(f)}>{f.facility_name ?? f.name}</Option>)}
        </Select>
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 18, color: "#123A63" }}>
          <LeftCircleFilled role="button" aria-label="Previous year" onClick={() => stepYear(-1)} style={{ cursor: "pointer" }} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>{year}</span>
          <RightCircleFilled role="button" aria-label="Next year" onClick={() => stepYear(1)} style={{ cursor: "pointer" }} />
        </div>
        <Button type="primary" loading={loading} onClick={() => load(year)}>Filter</Button>
      </div>

      <Spin spinning={loading}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          {COLUMNS.map((col, i) => {
            const tasks = columns?.[col.key] || [];
            return (
              <div key={col.key} style={{ padding: 16, borderLeft: i ? "1px solid #f0f0f0" : "none", minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "#1a1a2e", paddingBottom: 10, marginBottom: 16, borderBottom: "1px solid #e8eaee" }}>
                  {col.title}{columns ? ` (${tasks.length})` : ""}
                </div>
                {tasks.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#999" }}>No Data Available</div>
                ) : (
                  tasks.map((task) => <TaskCard key={task.id} task={task} />)
                )}
              </div>
            );
          })}
        </div>
      </Spin>
    </Card>
  );
};

export default StatusView;
